<template>
  <LcModal
    v-if="config"
    :close-triggers="['click-outside', 'close-button', 'escape']"
    :container-class="$style.modal"
    is-visible
    spacing="medium"
    :tracking="{
      action: 'play_high_frequency_game',
      topicKey: 'high-frequency-game-tutorial-modal',
    }"
    @close="emit('close')"
  >
    <template #header>
      <span class="inline-block w-full text-left headline-h4-headline">
        {{ $t('highfrequencygame.tutorial.headline') }}
      </span>
    </template>

    <div class="w-full flex flex-col gap-100 items-center">
      <LcCard
        v-for="({ headline, description }, index) in steps"
        :key="headline"
        body-class="pt-100 pr-800"
        :class="['tutorial-card', `tutorial-card-step-${index + 1}`, 'bg-secondary-secondary']"
        shadow="none"
      >
        <template #header>
          <h2 class="headline-h5-headline text-left">
            {{ headline }}
          </h2>
        </template>

        <p class="text-left">
          {{ description }}
        </p>
      </LcCard>
    </div>

    <p class="pt-200 text-left text-secondary-secondary">
      {{ $t('highfrequencygame.tutorial.hint') }}
    </p>

    <LcButton class="w-full mt-200" @click="emit('close')">
      {{ $t('highfrequencygame.tutorial.cta') }}
    </LcButton>
  </LcModal>
</template>

<script lang="ts" setup>
import { HFG_POSSIBLE_BONUS_ADD_ONS } from './highFrequencyGameConstants'

type Emits = {
  (e: 'close'): void
}

const emit = defineEmits<Emits>()

const { t } = useI18n()

const { config } = useHighFrequencyGame()

const addOn = computed(
  () =>
    config.value?.addOns.find((addOn) => HFG_POSSIBLE_BONUS_ADD_ONS.includes(addOn.name)) ?? null,
)

const numberOfSpots = computed(
  () =>
    config.value?.numbers.find(({ __typename }) => __typename === 'HighFrequency_NumbersConfig')
      ?.selectableMax,
)

const steps = computed(() => [
  {
    description: t('highfrequencygame.tutorial.step1.description', { value: numberOfSpots.value }),
    headline: t('highfrequencygame.tutorial.step1.headline'),
  },
  {
    description: t('highfrequencygame.tutorial.step2.description', {
      addOn: t(`highfrequencygame.addon.${normalizeKey(addOn.value?.name ?? '')}`),
      value: addOn.value?.maximumMultiplier,
    }),
    headline: t('highfrequencygame.tutorial.step2.headline'),
  },
  {
    description: t('highfrequencygame.tutorial.step3.description'),
    headline: t('highfrequencygame.tutorial.step3.headline'),
  },
])
</script>

<style lang="scss" module>
.modal {
  > * {
    max-width: 22.45rem;
  }
}
</style>

<style lang="scss" scoped>
.tutorial-card {
  background-position: right center;
  background-repeat: no-repeat;

  &-step-1 {
    background-image: url('./assets/tutorial-step-1.svg');
  }

  &-step-2 {
    background-image: url('./assets/tutorial-step-2.svg');
  }

  &-step-3 {
    background-image: url('./assets/tutorial-step-3.svg');
  }
}
</style>
